import { QUIZ_BATTLE_QUESTIONS_PER_LEVEL, type QuizBattleLevel, type QuizBattlePack } from '@shum/shared';
import type { Lang, Quiz } from './quizzes';

/**
 * Сборки квизов по темам: каждая сборка — 3–4 уровня,
 * уровень — один квиз (обрезанный до 8 вопросов), от простого к сложному.
 */

interface QuizPackDef {
  id: string;
  emoji: string;
  titles: Record<Lang, string>;
  descs: Record<Lang, string>;
  /** id квизов в порядке уровней: от простого к сложному. */
  quizIds: string[];
}

export const QUIZ_PACK_DEFS: QuizPackDef[] = [
  {
    id: 'movies',
    emoji: '🎬',
    titles: { ru: 'Кино', en: 'Movies' },
    descs: { ru: 'От попкорна до Средиземья — четыре уровня по великому и любимому.', en: 'From popcorn flicks to Middle-earth — four levels of the beloved classics.' },
    quizIds: ['popcorn-night', 'capes-and-masks', 'far-galaxy', 'middle-earth'],
  },
  {
    id: 'tv',
    emoji: '📺',
    titles: { ru: 'Сериалы', en: 'TV Shows' },
    descs: { ru: 'Ситкомы, стриминг и драконы. Не отрываясь — все сезоны.', en: 'Sitcoms, streaming and dragons. Binge-worthy, all seasons.' },
    quizIds: ['sitcom-legends', 'yellow-family', 'streaming-era', 'fantasy-thrones'],
  },
  {
    id: 'games',
    emoji: '🎮',
    titles: { ru: 'Видеоигры', en: 'Video Games' },
    descs: { ru: 'От карманных хитов до миров, где ты умираешь снова и снова.', en: 'From pocket hits to worlds where you die over and over.' },
    quizIds: ['pocket-games', 'pixel-classics', 'open-worlds', 'you-died'],
  },
  {
    id: 'music',
    emoji: '🎤',
    titles: { ru: 'Музыка', en: 'Music' },
    descs: { ru: 'Караоке-хиты, поп, рок-легенды и строчка, которую все допоют.', en: 'Karaoke hits, pop, rock legends and the line everyone finishes.' },
    quizIds: ['karaoke-classics', 'pop-twenties', 'rock-legends', 'finish-the-line'],
  },
  {
    id: 'memes',
    emoji: '🗿',
    titles: { ru: 'Мемы', en: 'Memes' },
    descs: { ru: 'Мемы, эмодзи-шифры, логотипы и словарь брейн-рота.', en: 'Memes, emoji ciphers, logos and the brainrot dictionary.' },
    quizIds: ['memes-twenties', 'emoji-decoder', 'logo-hunt', 'subscribe-bell'],
  },
  {
    id: 'science',
    emoji: '🔬',
    titles: { ru: 'Наука', en: 'Science' },
    descs: { ru: 'Тело, мозг, физика и дальний космос — по нарастающей.', en: 'Body, brain, physics and deep space — difficulty ramps up.' },
    quizIds: ['body-manual', 'how-brain-works', 'physics-around', 'deep-space'],
  },
  {
    id: 'tech',
    emoji: '📡',
    titles: { ru: 'Технологии', en: 'Tech' },
    descs: { ru: 'Соцсети, гаджеты прошлого, история интернета и эпоха ИИ.', en: 'Social feeds, retro gadgets, internet history and the AI era.' },
    quizIds: ['social-feeds', 'gadget-museum', 'internet-history', 'ai-era'],
  },
  {
    id: 'sports',
    emoji: '⚽',
    titles: { ru: 'Спорт', en: 'Sports' },
    descs: { ru: 'Футбол, кольцо и шайба, олимпийские рекорды и Королевские гонки.', en: 'Football, hoops and pucks, Olympic records and Royal races.' },
    quizIds: ['goal-time', 'hoop-and-puck', 'olympic-record', 'f1-royal'],
  },
  {
    id: 'geo',
    emoji: '🗺️',
    titles: { ru: 'География', en: 'Geography' },
    descs: { ru: 'Места силы, карта от Балтики до Камчатки, флаги и столицы.', en: 'Landmarks, the map from Baltic to Kamchatka, flags and capitals.' },
    quizIds: ['world-landmarks', 'russia-map', 'flag-mania', 'capitals-hard'],
  },
  {
    id: 'history',
    emoji: '🏛️',
    titles: { ru: 'История', en: 'History' },
    descs: { ru: 'Боги и гидры, древний мир, Средневековье и век в огне.', en: 'Gods and hydras, the ancient world, the Middle Ages and a century on fire.' },
    quizIds: ['mythology-gods', 'ancient-world', 'middle-ages', 'century-on-fire'],
  },
  {
    id: 'nature',
    emoji: '🌿',
    titles: { ru: 'Природа', en: 'Nature' },
    descs: { ru: 'Звери, динозавры, глубины океана и погода за окном.', en: 'Animals, dinosaurs, ocean depths and the weather outside.' },
    quizIds: ['animal-kingdom', 'dino-era', 'ocean-deep', 'weather-climate'],
  },
  {
    id: 'food',
    emoji: '🍕',
    titles: { ru: 'Еда', en: 'Food' },
    descs: { ru: 'Кухни мира, сладкая жизнь, кофе или чай и барная карта.', en: 'World kitchens, the sweet life, coffee or tea and the bar menu.' },
    quizIds: ['world-kitchens', 'sweet-life', 'coffee-or-tea', 'bar-menu'],
  },
  {
    id: 'trivia',
    emoji: '🧠',
    titles: { ru: 'Эрудиция', en: 'Trivia' },
    descs: { ru: 'Верю — не верю, про всё сразу, классика и хардкор-микс на десерт.', en: 'Believe it or not, a bit of everything, classics and a hardcore finish.' },
    quizIds: ['believe-or-not', 'everything-at-once', 'school-classics', 'hardcore-mix'],
  },
  {
    id: 'anime',
    emoji: '🌸',
    titles: { ru: 'Аниме', en: 'Anime' },
    descs: { ru: 'Гибли, сёнэн-марафон, вся аниме-вселенная и золотая классика.', en: 'Ghibli, shonen marathon, the whole anime universe and the classics.' },
    quizIds: ['ghibli-magic', 'shonen-marathon', 'anime-universe', 'anime-classics'],
  },
  {
    id: 'culture',
    emoji: '🎨',
    titles: { ru: 'Культура', en: 'Culture' },
    descs: { ru: 'Мировая классика, галереи, архитектура и русская литература.', en: 'World classics, galleries, architecture and Russian literature.' },
    quizIds: ['world-literature', 'art-gallery', 'architecture-quiz', 'russian-classics'],
  },
  {
    id: 'nostalgia',
    emoji: '📼',
    titles: { ru: 'Ностальгия', en: 'Nostalgia' },
    descs: { ru: 'Дети девяностых, хиты нулевых, сделано в СССР и консольная археология.', en: 'Kids of the 90s, noughties hits, made in the USSR and console archaeology.' },
    quizIds: ['kids-of-the-90s', 'noughties-hits', 'made-in-ussr', 'retro-consoles'],
  },
];

/** Собирает пак для отправки в комнату из квизов выбранной локали. */
export function buildQuizPack(def: QuizPackDef, quizzes: Quiz[], lang: Lang): QuizBattlePack | null {
  const levels: QuizBattleLevel[] = [];
  for (const quizId of def.quizIds) {
    const quiz = quizzes.find((q) => q.id === quizId);
    if (!quiz) continue;
    levels.push({
      id: quiz.id,
      title: quiz.title,
      emoji: quiz.emoji,
      difficulty: quiz.difficulty,
      questions: quiz.questions.slice(0, QUIZ_BATTLE_QUESTIONS_PER_LEVEL).map((q) => ({
        q: q.q,
        options: q.options,
        correct: q.correct,
        fact: q.fact,
      })),
    });
  }
  if (levels.length === 0) return null;
  return {
    id: def.id,
    title: def.titles[lang],
    emoji: def.emoji,
    desc: def.descs[lang],
    lang,
    levels,
  };
}

export function packLevelCount(def: QuizPackDef): number {
  return def.quizIds.length;
}

export function packQuestionsCount(def: QuizPackDef): number {
  return def.quizIds.length * QUIZ_BATTLE_QUESTIONS_PER_LEVEL;
}
